/* eslint-disable @nx/enforce-module-boundaries */
import React, { useState, useEffect, useRef } from 'react';
import EventsListingLayout from 'v4/snippets/eventsV2/layout/listing';
import { Tabs, TabList, TabPanels, Tab, TabPanel } from '@mybridge/ui/tabs';
import {
  Box,
  Text,
  Image,
  HStack,
  Button,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuItemOption,
  MenuGroup,
  MenuOptionGroup,
  MenuDivider,
  Container,
  Stack,
  VStack
} from '@mybridge/ui';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import {
  listUpcomingEvents,
  listPastEvents,
} from 'v4/store/actions/post.actions';
import { useInfiniteQuery } from '@tanstack/react-query';
import { flattenQueryResults, getNextPageFromURL } from 'v4/utils/utils';
import SectionLoader from 'v4/components/common/loader/sectionLoader';
import { Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import moment from 'moment-timezone';
import {
  EyeViewIcon,
  Pencil,
  ThreeDotsIcon,
  VerticalThreeDot,
} from '@mybridge/icons';
import { Tooltip } from '@mybridge/ui';
import EventListRow from 'v4/snippets/eventsV2/components/list-row';
import EventListBox from 'v4/snippets/eventsV2/components/list-box';
import { isMobile } from 'react-device-detect';
import { currencyFormatter } from 'v4/components/utils';
import { SiteShortcutLinks } from 'v4/snippets/dashboard/site-shortcut-links';
import CalendarSidebar from 'v4/snippets/calendar/calendar-sidebar';
import { DashboardMyCircle } from 'v4/snippets/dashboard/circle';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { ANALYTICS_EVENTS, trackEvent } from 'v4/utils/analyticsEvents';
// import styles from 'v4/components/header/status-dropdown/statusProfile.module.scss'

const EventMainPage = () => {
  const dispatch = useDispatch();
  const router = useRouter();
  const [tabIndex, setTabIndex] = useState(0);
  const [eventUpdated, setEventUpdated] = useState(false);
  // const { upcomingEventsList, pastEventsList, loading } = useSelector((state) => state.posts);
  const { userProfileInfo } = useSelector((state) => state.userProfile);
  const {
    data: upcomingEventsList_,
    isFetching: upcomingEventsListFetching,
    isLoading: upcomingEventsListLoading,
    refetch: refetchUpcomingListing,
    fetchNextPage: upcomingFetchNextPage,
    hasNextPage: upcomingHasNextPage,
  } = useInfiniteQuery({
    queryKey: ['upcoming-events'],
    queryFn: async ({ pageParam }) => {
      const resp = (await dispatch(listUpcomingEvents({ pageParam })))?.payload;
      return resp;
    },
    getNextPageParam: (lastPage) => getNextPageFromURL(lastPage?.next),
    refetchOnWindowFocus: false,
    enabled: tabIndex === 0,
  });

  const upcomingEventsList = flattenQueryResults(upcomingEventsList_);
  const upcomingListCount = upcomingEventsList_?.pages?.[0]?.count;
  const upcomingListPagingCount = upcomingEventsList_?.pages?.[0]?.length * 10;

  const {
    data: pastEventsList_,
    isFetching: pastEventsListFetching,
    isLoading: pastEventsListLoading,
    refetch: refetchPastListing,
    fetchNextPage: pastFetchNextPage,
    hasNextPage: pastHasNextPage,
  } = useInfiniteQuery({
    queryKey: ['past-events'],
    queryFn: async ({ pageParam }) => {
      const resp = (await dispatch(listPastEvents({ pageParam })))?.payload;
      return resp;
    },
    getNextPageParam: (lastPage) => getNextPageFromURL(lastPage?.next),
    refetchOnWindowFocus: false,
    enabled: tabIndex === 1,
  });
  const { t } = useTranslator();
  const pastEventsList = flattenQueryResults(pastEventsList_);
  const pastEventsListCount = pastEventsList_?.pages?.[0]?.count;
  const pastEventsListPagingCount = pastEventsList_?.pages?.[0]?.length * 10;

  /*
  useEffect(() => {
    if (tabIndex === 0) {
      refetchUpcomingListing();
    } else if (tabIndex === 1) {
      refetchPastListing();
    }
  }, [tabIndex]);
  */


  useEffect(() => {
    if (eventUpdated) {
      refetchUpcomingListing();
      refetchPastListing();
      // setTabIndex(0);
      setEventUpdated(false);
    }
  }, [eventUpdated]);

  const handleTabsChange = (index) => {
    setTabIndex(index);
  };

  const hasTrackedPageVisit = useRef(false);

  const getUserSlug = () => {
    return userProfileInfo?.slug || userProfileInfo?.username || null;
  };
  
  const trackMyEventsPageVisit = () => {
    const userSlug = getUserSlug();
    if (!userSlug) return;

    trackEvent(ANALYTICS_EVENTS.MyEvents, userSlug, {
      page_path: router.asPath,
      page_title: 'My Events',
      active_tab: tabIndex === 0 ? 'upcoming' : 'past',
      upcoming_events_count: upcomingListCount || 0,
      past_events_count: pastEventsListCount || 0,
      device_type: !isMobile ? 'desktop' : 'mobile',
      timestamp: new Date().toISOString()
    });
  };

  useEffect(() => {
    if (userProfileInfo && !hasTrackedPageVisit.current) {
      trackMyEventsPageVisit();
      hasTrackedPageVisit.current = true;
    }
  }, [userProfileInfo, upcomingListCount, pastEventsListCount, tabIndex]);

  return (
    <Container w={!isMobile ? '1320px' : '1000px'}
      maxW={!isMobile ? '100%' : '100%'}
      p={!isMobile ? ['5px 10px 0', 0] : [0, 2, 4]}
      bg="white"
      h="100%">
      <HStack minH={!isMobile && "120vh"} alignItems="stretch" spacing={0} bg="white" flexDirection={isMobile ? "column" : "row"}  >
        {!isMobile && (
          <Stack
          display={['none', 'none', 'flex']}
          pos="sticky"
          top="64px"
          spacing={2}
          minH="90vh"
          borderBottom="1px solid #e0e0e094"
          height={'500px'}
          zIndex="999"
        >
          <VStack
            spacing={2}
            justifyContent="space-between"
            height="100%"
            align="stretch"
            h="calc(100vh - 0px)"
            backgroundColor="rgb(255, 255, 255)"
          >
            <VStack spacing={2} align="stretch" h="100%" flex="1">
              <SiteShortcutLinks />
              <Box 
              //  className={styles.hideTitle}
              >
                <DashboardMyCircle h="300px" flex="1" />
              </Box>
            </VStack>
          </VStack>
        </Stack>
        )}
        <Box pos={!isMobile && "sticky"} top={!isMobile && "64px"} h={!isMobile && "100%"} p={isMobile ? "4%" : "1%"}>
          <CalendarSidebar isDashboard={false} />
        </Box>
        <EventsListingLayout displayHeader>
          <Box pt={['20px', '10px']}>
            <Tabs
              isFitted
              variant="myBridgeVariant"
              index={tabIndex}
              onChange={handleTabsChange}
              isLazy
            >
              <TabList>
                <Tab>{t('myEvents.upcoming') || "Upcoming"}</Tab>
                <Tab>{t('myEvents.past') || "Past"}</Tab>
              </TabList>

              <TabPanels>
                {/* Upcoming Events listing */}
                <TabPanel px={0}>
                  <>
                    {upcomingEventsListLoading || upcomingEventsListFetching ? (
                      <SectionLoader />
                    ) : (
                      <>
                      {upcomingListCount > 0 ? (
                        <>
                          <Box display="flex" flexWrap="wrap" gap="20px" justifyContent="flex-start">
                            {upcomingEventsList?.map((event, i) => (
                              <EventListBox
                                event={event}
                                key={i}
                                type="upcoming"
                                isDeleted={(e) => setEventUpdated(e)}
                              />
                            ))}
                          </Box>

                          {upcomingHasNextPage && (
                            <HStack width="100%" justifyContent="center">
                              <Button onClick={() => upcomingFetchNextPage()}>
                                {t('myEvents.loadMore') || "Load More"}
                              </Button>
                            </HStack>
                          )}
                          {/* {upcomingHasNextPage && (
                            <HStack width="100%" justifyContent="center" mt="20px">
                              <Button onClick={() => pastFetchNextPage()}>Load More</Button>
                            </HStack>
                          )} */}
                        </>
                      ) : (
                        <Box py="50px" textAlign="center" align="center">
                          <Text mb={5} color="#3D5A80" fontSize="13px">
                            {t('myEvents.myBridgeEventsIntro1') || "myBridge Events is a great way to present effectively to large audiences."}
                          </Text>
                          <Text mb={5} color="#3D5A80" fontSize="13px">
                            {t('myEvents.myBridgeEventsIntro2') || "Easily host marketing events, product launches, training sessions, town halls and more."}
                          </Text>
                          <Text mb={5} color="#3D5A80" fontSize="13px">
                            {t('myEvents.myBridgeEventsIntro3') || "Schedule your first event today."}
                          </Text>
                          <Image src="/images/defaultEmptyEvent.svg" alt="No Events" m="0 auto" />
                        </Box>
                      )}
                    </>
                    )}
                  </>
                </TabPanel>
                {/* Past events listing */}
                <TabPanel px={0}>
                  <>
                    {pastEventsListLoading || pastEventsListFetching ? (
                      <SectionLoader />
                    ) : (
                      <>
                        {pastEventsListCount > 0 ? (
                          <>
                            <Box display="flex" flexWrap="wrap" gap="20px" justifyContent="flex-start">
                              {pastEventsList?.map((event, i) => (
                                <EventListBox
                                  event={event}
                                  key={i}
                                  type="past"
                                  isDeleted={(e) => setEventUpdated(e)}
                                />
                              ))}
                            </Box>
                            {pastHasNextPage && (
                              <HStack width="100%" justifyContent="center" mt="20px">
                                <Button onClick={() => pastFetchNextPage()}>{t('myEvents.loadMore') || "Load More"}</Button>
                              </HStack>
                            )}
                          </>
                        ) : (
                          <Box py="50px" textAlign="center" align="center">
                            <Text mb={5} color="brandPrimary.500" fontSize="13px">
                              {t('myEvents.myBridgeEventsIntro1') || "myBridge Events is a great way to present effectively to large audiences."}
                            </Text>
                            <Text mb={5} color="brandPrimary.500" fontSize="13px">
                              {t('myEvents.myBridgeEventsIntro2') || "Easily host marketing events, product launches, training sessions, town halls and more."}
                            </Text>
                            <Text mb={5} color="brandPrimary.500" fontSize="13px">
                              {t('myEvents.myBridgeEventsIntro3') || "Schedule your first event today."}
                            </Text>
                            <Image src="/images/defaultEmptyEvent.svg" alt="No Events" m="0 auto" />
                          </Box>
                        )}
                      </>
                    )}
                  </>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        </EventsListingLayout>
      </HStack>
    </Container>
  );
};

export default EventMainPage;
